import { useState, useEffect, useCallback, useRef } from 'react';
import { Examen } from '../types/examenes';
import { fetchExamenes, createExamen, FetchExamenesParams } from '../services/examenesService';

interface UseExamenesReturn {
  examenes: Examen[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  search: string;
  categoria: string;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setCategoria: (categoria: string) => void;
  loadExamenes: () => Promise<void>;
  addExamen: (examen: Omit<Examen, '_id'>) => Promise<Examen>;
}

export const useExamenes = (initialLimit = 25): UseExamenesReturn => {
  const [examenes, setExamenes] = useState<Examen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const limitRef = useRef(initialLimit);

  // Debounce search to avoid fetching on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoria]);

  const buildParams = useCallback((): FetchExamenesParams => {
    const params: FetchExamenesParams = {
      page,
      limit: limitRef.current,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (categoria) params.categoria = categoria;
    return params;
  }, [page, debouncedSearch, categoria]);

  const loadExamenes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchExamenes(buildParams());
      setExamenes(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError('Error al cargar exámenes');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadExamenes();
  }, [loadExamenes]);

  const addExamen = async (examenData: Omit<Examen, '_id'>) => {
    try {
      const nuevoExamen = await createExamen(examenData);
      // Reload current page to reflect new item
      await loadExamenes();
      return nuevoExamen;
    } catch (err) {
      setError('Error al crear examen');
      throw err;
    }
  };

  // Wrappers to trigger page resets on filter changes
  const handleSetSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleSetCategoria = useCallback((value: string) => {
    setCategoria(value);
  }, []);

  return {
    examenes,
    loading,
    error,
    total,
    page,
    totalPages,
    search,
    categoria,
    setPage,
    setSearch: handleSetSearch,
    setCategoria: handleSetCategoria,
    loadExamenes,
    addExamen,
  };
};